<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { useLeadSubmissionsStore } from 'dashboard/stores/leadSubmissions';
import ConvertDialog from './ConvertDialog.vue';

const props = defineProps({
  submission: { type: Object, default: null },
});
const emit = defineEmits(['close', 'converted', 'discarded', 'reprocessed']);
const { t } = useI18n();
const store = useLeadSubmissionsStore();

const dialogRef = ref(null);
const convertDialogRef = ref(null);
const discardReason = ref('');
const showDiscardForm = ref(false);

const isActing = computed(() => props.submission && store.actingOn === props.submission.id);

function open() { dialogRef.value?.open(); }
function close() { emit('close'); dialogRef.value?.close(); showDiscardForm.value = false; discardReason.value = ''; }

defineExpose({ open });

async function handleReprocess() {
  try {
    await store.reprocess(props.submission.id);
    useAlert(t('LEADS.INBOX.REPROCESS_TOAST.SUCCESS'));
    emit('reprocessed');
    close();
  } catch {
    useAlert(t('LEADS.INBOX.REPROCESS_TOAST.ERROR'));
  }
}

async function handleDiscard() {
  if (!discardReason.value.trim()) return;
  try {
    await store.discard(props.submission.id, discardReason.value);
    useAlert(t('LEADS.INBOX.DISCARD_TOAST.SUCCESS'));
    emit('discarded');
    close();
  } catch {
    useAlert(t('LEADS.INBOX.DISCARD_TOAST.ERROR'));
  }
}

function openConvert() {
  convertDialogRef.value?.open(props.submission);
}

function onConverted() {
  emit('converted');
  close();
}

function formatJson(val) {
  if (!val) return '-';
  if (typeof val === 'string') {
    try { return JSON.stringify(JSON.parse(val), null, 2); } catch { return val; }
  }
  return JSON.stringify(val, null, 2);
}

const tabs = [
  { key: 'normalized', label: 'Dados normalizados' },
  { key: 'raw',       label: 'Payload bruto' },
  { key: 'ai',        label: 'Classificação IA' },
  { key: 'routing',   label: 'Regras' },
];
const activeTab = ref('normalized');
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('LEADS.INBOX.ACTIONS.OPEN')"
    type="right_panel"
    :on-close="close"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <span
          class="inline-flex px-2 py-0.5 rounded text-label-small font-medium uppercase"
          :class="{
            'bg-n-amber-4/30 text-n-amber-11': submission?.status === 'pending_triage',
            'bg-n-ruby-4/30 text-n-ruby-11': submission?.status === 'error',
            'bg-n-blue-4/30 text-n-blue-11': submission?.status === 'processing',
            'bg-n-green-4/30 text-n-green-11': submission?.status === 'received',
          }"
        >
          {{ t(`LEADS.INBOX.STATUS.${submission?.status?.toUpperCase()}`) }}
        </span>
        <span class="text-body-small text-n-slate-11">{{ submission ? new Date(submission.received_at).toLocaleString('pt-BR') : '' }}</span>
      </div>
    </template>

    <div class="flex flex-col gap-4">
      <!-- Tabs -->
      <div class="border-b border-n-weak">
        <nav class="flex gap-4">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="pb-2 text-body-small font-medium border-b-2 -mb-px transition-colors"
            :class="activeTab === tab.key ? 'text-n-blue-11 border-n-blue-11' : 'text-n-slate-11 border-transparent hover:text-n-slate-12'"
            @click="activeTab = tab.key"
          >{{ tab.label }}</button>
        </nav>
      </div>

      <!-- Tab content -->
      <div v-if="activeTab === 'normalized'" class="flex flex-col gap-3">
        <div v-for="[k, v] in Object.entries(submission?.normalized_data || {})" :key="k" class="flex gap-3 text-body-small">
          <span class="text-n-slate-11 min-w-28 shrink-0 capitalize">{{ k.replace(/_/g, ' ') }}:</span>
          <span class="text-n-slate-12">{{ v ?? '-' }}</span>
        </div>
      </div>

      <div v-else-if="activeTab === 'raw'" class="flex flex-col gap-3">
        <pre class="text-body-small text-n-slate-11 bg-n-alpha-2 rounded p-3 overflow-auto max-h-64 font-mono">{{ formatJson(submission?.raw_payload) }}</pre>
      </div>

      <div v-else-if="activeTab === 'ai'" class="flex flex-col gap-3">
        <div v-for="[k, v] in [['Intent', submission?.intent_name], ['Confidence', submission?.classification_confidence ? `${Math.round(submission.classification_confidence * 100)}%` : null], ['Class', submission?.classification], ['Summary', submission?.ai_summary]]" :key="k" class="flex gap-3 text-body-small">
          <span class="text-n-slate-11 min-w-28 shrink-0">{{ k }}:</span>
          <span class="text-n-slate-12">{{ v ?? '-' }}</span>
        </div>
      </div>

      <div v-else-if="activeTab === 'routing'" class="flex flex-col gap-3">
        <pre class="text-body-small text-n-slate-11 bg-n-alpha-2 rounded p-3 overflow-auto max-h-64 font-mono">{{ formatJson(submission?.routing_rules) }}</pre>
      </div>

      <!-- Discard form -->
      <div v-if="showDiscardForm" class="flex flex-col gap-2 p-4 border border-n-weak rounded-lg">
        <label class="text-body-small font-medium text-n-slate-12">{{ t('LEADS.INBOX.DISCARD_DIALOG.REASON_LABEL') }}</label>
        <textarea
          v-model="discardReason"
          rows="3"
          :placeholder="t('LEADS.INBOX.DISCARD_DIALOG.REASON_PLACEHOLDER')"
          class="px-3 py-2 text-body-small rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:border-n-brand resize-none"
        />
        <div class="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" :label="t('GENERAL.CANCEL')" @click="showDiscardForm = false" />
          <Button size="sm" color="ruby" :label="t('LEADS.INBOX.DISCARD_DIALOG.CONFIRM')" :is-loading="isActing" @click="handleDiscard" />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between gap-3">
        <Button
          v-if="!showDiscardForm"
          size="sm"
          color="ruby"
          variant="ghost"
          :label="t('LEADS.INBOX.ACTIONS.DISCARD')"
          :is-loading="isActing"
          @click="showDiscardForm = true"
        />
        <div class="flex gap-2 ml-auto">
          <Button size="sm" variant="ghost" :label="t('LEADS.INBOX.ACTIONS.REPROCESS')" :is-loading="isActing" @click="handleReprocess" />
          <Button size="sm" color="blue" :label="t('LEADS.INBOX.ACTIONS.CONVERT')" :is-loading="isActing" @click="openConvert" />
        </div>
      </div>
    </template>
  </Dialog>

  <ConvertDialog ref="convertDialogRef" @converted="onConverted" />
</template>